import React, { useEffect, useState } from 'react';
import { EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { GetAccountInfoResponse } from '@cbinsights/salesforceservice/salesforceservice';
import { UpdateTeamRequest } from '@cbinsights/adminservice/adminservice';
import {
  Button,
  Descriptions,
  Form,
  Input,
  Modal,
  Spin,
  Tooltip,
  message,
} from 'antd';
import { getSalesforceAccountInfo } from '../../services/api/GetSalesforceAccountInfo';

type Props = {
  salesforceAccountId: string;
  teamId: number;
  updateTeam: (
    data: Partial<UpdateTeamRequest>,
    teamId: number
  ) => Promise<void>;
  refetchGetTeam: () => void;
};

const UpdateSalesforceAccount = ({
  salesforceAccountId,
  teamId,
  updateTeam,
  refetchGetTeam,
}: Props) => {
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [accountInfo, setAccountInfo] = useState<GetAccountInfoResponse | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [pendingValue, setPendingValue] = useState<string>('');

  const [form] = Form.useForm();

  useEffect(() => {
    if (salesforceAccountId) {
      form.setFieldsValue({ salesforceAccountId });
    }
  }, [salesforceAccountId, form]);

  const handleConfirmUpdate = async (accountId: string) => {
    try {
      await updateTeam(
        {
          salesforce_account_id: accountId,
        },
        teamId
      );
      message.success('Success! The Salesforce Account ID has been updated.');
      refetchGetTeam();
      setShowModal(false);
      setShowConfirmModal(false);
      setAccountInfo(null);
      setPendingValue('');
    } catch (error) {
      message.error(
        'Failed to update the Salesforce Account ID. Please try again.'
      );
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setAccountInfo(null);
    setPendingValue('');
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const newId = values.salesforceAccountId?.trim() || '';

      if (!newId) {
        setPendingValue('');
        setAccountInfo(null);
        Modal.confirm({
          title: 'Clear Salesforce Account ID?',
          icon: <ExclamationCircleOutlined />,
          content:
            'Are you sure you want to remove the Salesforce Account ID from this team?',
          okText: 'Yes, Clear',
          cancelText: 'Cancel',
          onOk: () => handleConfirmUpdate(''),
        });
        return;
      }

      setIsLoading(true);
      try {
        const info = await getSalesforceAccountInfo({ accountId: newId });

        if (!info) {
          message.error(
            'Invalid Salesforce Account ID. The account could not be found.'
          );
          setIsLoading(false);
          return;
        }

        setAccountInfo(info);
        setPendingValue(newId);
        setShowConfirmModal(true);
        setIsLoading(false);
      } catch (error) {
        message.error(
          'Invalid Salesforce Account ID. Please verify the ID exists in Salesforce.'
        );
        setIsLoading(false);
      }
    } catch {
      // Validation failed, we are showing form erros here
    }
  };

  return (
    <div>
      <Tooltip title="Update Salesforce Account">
        <div className="text-blue-500">
          <EditOutlined onClick={() => setShowModal(true)} />
        </div>
      </Tooltip>
      <Modal
        title={
          salesforceAccountId
            ? `Update Salesforce Account ID: ${salesforceAccountId}`
            : 'Add Salesforce Account ID'
        }
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
      >
        <Form form={form}>
          <Form.Item name="salesforceAccountId">
            <Input placeholder="Enter Salesforce Account ID" />
          </Form.Item>
          <Button type="primary" onClick={handleSubmit} loading={isLoading}>
            Save
          </Button>
        </Form>
      </Modal>

      <Modal
        title="Confirm Account Details"
        open={showConfirmModal}
        onOk={() => handleConfirmUpdate(pendingValue)}
        onCancel={handleCancelConfirm}
        okText="Confirm & Update"
        cancelText="Cancel"
      >
        {accountInfo ? (
          <>
            <p>Please review the account details before confirming:</p>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Account ID">
                {accountInfo.accountId}
              </Descriptions.Item>
              <Descriptions.Item label="Account Name">
                {accountInfo.accountName}
              </Descriptions.Item>
              <Descriptions.Item label="Owner">
                {accountInfo.accountOwner}
              </Descriptions.Item>
            </Descriptions>
          </>
        ) : (
          <Spin />
        )}
      </Modal>
    </div>
  );
};

export default UpdateSalesforceAccount;
